import React, {
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Typography } from "@mui/material";
import PageLayoutBase from "@/components/layouts/page/PageLayout";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import useFetcher from "@/helpers/hooks/useFetcher";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import BrandDigitalCampaignListBase from "../components/BrandDigitalCampaignList";

type BrandDigitalCampaignFilters = {
  pageSize: number;
  pageIndex: number;
  searchKey: string;
  sortBy: string;
  sortByDir: string;
};

type BrandDigitalCampaignListResponse = {
  result?: Array<Record<string, unknown>>;
  pageCount?: number;
  recordsTotal?: number;
};

type BrandDigitalCampaignContext = {
  selectedBrand?: {
    id?: string | number;
  } | null;
  loggedInUser?: {
    id?: string | number;
  } | null;
};

const PageLayout = PageLayoutBase as React.ComponentType<any>;
const BrandDigitalCampaignList =
  BrandDigitalCampaignListBase as React.ComponentType<any>;

const BrandDigitalCampaignListPage = () => {
  const { selectedBrand, loggedInUser } = useContext(
    GlobalContext
  ) as BrandDigitalCampaignContext;
  const loader = useContext(LoaderContext);
  const [filters, setFilters] = useState<BrandDigitalCampaignFilters>({
    pageSize: 10,
    pageIndex: 0,
    searchKey: "",
    sortBy: "",
    sortByDir: "",
  });

  const reqData = useMemo(
    () => ({
      id: selectedBrand?.id,
      userId: loggedInUser?.id,
      row: filters?.pageSize,
      page: filters?.pageIndex + 1,
      searchKey: filters?.searchKey || "",
      sortBy: filters?.sortBy || "",
      sortByDir: filters?.sortByDir || "",
    }),
    [selectedBrand?.id, loggedInUser?.id, filters]
  );

  const { data, isLoading, isValidating } =
    useFetcher<BrandDigitalCampaignListResponse>(
      reqData,
      campaignService.getCampaignReportListing,
      { revalidateOnFocus: false }
    );

  const campaignReportList = data?.result || [];
  const pageCount = data?.pageCount || 0;
  const totalRecords = data?.recordsTotal;

  useEffect(() => {
    loader(isLoading || isValidating);
  }, [isLoading, loader, isValidating]);

  const fetchCampaignsReportData = useCallback(
    (data: Partial<BrandDigitalCampaignFilters>) => {
      setFilters((prev) => ({ ...prev, ...data }));
    },
    []
  );

  useEffect(() => {
    if (selectedBrand) {
      fetchCampaignsReportData({ pageSize: 10, pageIndex: 0, searchKey: "" });
    }
  }, [selectedBrand, fetchCampaignsReportData]);

  return (
    <PageLayout className="admin-dashboard-container mx-0 mx-sm-2">
      <div className="dashboard participant-dashboard">
        <Typography variant="h5" className="widget-title mb-4 mb-md-auto">
          Digital Campaigns
        </Typography>
        <BrandDigitalCampaignList
          campaignReportList={campaignReportList}
          pageCount={pageCount}
          fetchCampaignsReportData={fetchCampaignsReportData}
          isLoading={isLoading}
          totalRecords={totalRecords}
        />
      </div>
    </PageLayout>
  );
};

export default BrandDigitalCampaignListPage;
